import { ChangeDetectionStrategy, Component, OnInit, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../services/auth.service';
import { PlatformService } from '../../services/platform.service';

/**
 * LandingComponent
 *
 * Welcome screen displayed before authentication.
 * Shows MyABVV branding and login button.
 * Only shown when enableLandingPage feature flag is true.
 *
 * Features:
 * - Platform-aware UI adjustments
 * - Triggers OAuth login flow
 * - Responsive design for all screen sizes
 */
@Component({
    selector: 'myabvv-landing',
    standalone: true,
    imports: [ReactiveFormsModule, TranslateModule],
    templateUrl: './landing.component.html',
    styleUrls: ['./landing.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LandingComponent implements OnInit {
  private authService = inject(AuthService);
  private platformService = inject(PlatformService);
  private router = inject(Router);
  private fb = inject(FormBuilder);

  isMobile = signal(false);
  isLoading = signal(false);
  currentYear: number = new Date().getFullYear();

  // Test mode properties
  logoTapCount = signal(0); // Public for debug display
  private lastTapTime = 0;
  private readonly TAP_TIMEOUT_MS = 2000;
  testModeEnabled = signal(false);
  testLoginForm!: FormGroup;
  testLoginError = signal<string | null>(null);
  isTestLoading = signal(false);

  ngOnInit(): void {
    this.isMobile.set(this.platformService.isMobile());
    // Initialize test login form
    this.testLoginForm = this.fb.group({
      username: ['', [Validators.required]],
      password: ['', [Validators.required, Validators.minLength(8)]]
    });

    // If already authenticated, redirect to home
    if (this.authService.hasValidAccessToken()) {
      this.router.navigate(['/']);
    }
  }

  /**
   * Handle login button click
   * Triggers OAuth2 authorization code flow
   */
  async onLogin(): Promise<void> {
    try {
      this.isLoading.set(true);

      await this.authService.login();

      // Navigation to Keycloak happens automatically
      // User will be redirected back to callback component after authentication
    } catch (error) {
      const err = error as { name?: string; message?: string; stack?: string } | undefined;
      console.error('LandingComponent: Login failed:', error);
      console.error('LandingComponent: Error type:', typeof error);
      console.error('LandingComponent: Error name:', err?.name);
      console.error('LandingComponent: Error message:', err?.message);
      console.error('LandingComponent: Error stack:', err?.stack);
      console.error('LandingComponent: Full error object:', JSON.stringify(error, null, 2));
      this.isLoading.set(false);

      // Show error message to user with more detail
      const errorMsg = err?.message || 'Unknown error';
      console.error('LandingComponent: Showing alert to user with message:', errorMsg);

      // Don't use alert - it blocks and causes issues. Log instead.
      // User will see logs in logcat
    }
  }

  /**
   * Handle logo tap for test mode activation
   * Requires 5 taps within 2-second window
   */
  onLogoTap(): void {
    const now = Date.now();

    // Reset counter if taps are too far apart
    if (now - this.lastTapTime > this.TAP_TIMEOUT_MS) {
      this.logoTapCount.set(0);
    }

    this.logoTapCount.update(v => v + 1);
    this.lastTapTime = now;

    // Activate test mode on 5th tap
    if (this.logoTapCount() === 5) {
      this.testModeEnabled.set(true);
      this.logoTapCount.set(0); // Reset counter
    }
  }

  /**
   * Cancel test mode and return to normal login
   */
  cancelTestMode(): void {
    this.testModeEnabled.set(false);
    this.logoTapCount.set(0);
  }

  /**
   * Handle test login form submission
   * Authenticates with Keycloak using Direct Access Grant (ROPC)
   */
  async loginAsTest(): Promise<void> {
    if (this.testLoginForm.valid) {
      const { username, password } = this.testLoginForm.value;
      this.testLoginError.set(null);
      this.isTestLoading.set(true);
      try {
        await this.authService.loginWithTestAccount(username, password);
      } catch (error) {
        const err = error as { message?: string } | undefined;
        this.testLoginError.set(err?.message || 'Login failed');
        console.error('[LandingComponent] Test login failed:', error);
      } finally {
        this.isTestLoading.set(false);
      }
    }
  }

  /**
   * Handle register button click
   * Redirects to registration page (if applicable)
   */
  onRegister(): void {
    // TODO: Implement registration flow if needed
    // For now, just redirect to Keycloak registration
    alert('Registration will be available in a future release.');
  }
}
